import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Copy, Sparkles } from "lucide-react";

const samples = [
  "Chartered accountant audit & assurance services",
  "GST return filing professional service",
  "Software development services",
  "Cotton t-shirt for retail",
  "Wheat flour 1kg packaging",
  "Mobile phone with charger",
];

export default function HSNFinder() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (query) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/hsn/lookup", { description: text });
      setResult(data);
    } catch (err) {
      toast.error("Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[#002FA7]" />
          <p className="label-caps text-[#002FA7]">AI-Powered</p>
        </div>
        <h1 className="font-display font-black text-4xl tracking-tighter">HSN / SAC Finder</h1>
        <p className="text-zinc-600 mt-2">Describe any product or service — get the correct HSN/SAC code and GST rate instantly.</p>
      </div>

      <div className="bg-white border border-zinc-200 p-6 mb-6">
        <label className="label-caps block mb-2">What are you billing for?</label>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && lookup()}
            placeholder="e.g. GST filing service, cotton t-shirt, audit report..."
            data-testid="hsn-input"
            className="flex-1 border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900"
          />
          <button onClick={() => lookup()} disabled={busy || !q.trim()} data-testid="hsn-search" className="bg-[#002FA7] text-white px-5 py-2.5 font-semibold flex items-center gap-2 hover:bg-blue-800 disabled:opacity-50">
            <Search size={16} /> {busy ? "Searching..." : "Find Code"}
          </button>
        </div>
        <div className="mt-4">
          <p className="text-xs text-zinc-500 mb-2">Try a sample:</p>
          <div className="flex flex-wrap gap-2">
            {samples.map(s => (
              <button key={s} onClick={() => { setQ(s); lookup(s); }} className="text-xs border border-zinc-300 bg-white px-3 py-1.5 hover:bg-zinc-50">{s}</button>
            ))}
          </div>
        </div>
      </div>

      {busy && (
        <div className="bg-[#EFF4FF] border border-[#002FA7] p-6 text-center">
          <span className="font-mono text-sm text-[#002FA7]">
            <span className="ai-loading-dot">●</span> <span className="ai-loading-dot">●</span> <span className="ai-loading-dot">●</span> Analysing with GPT-5.2...
          </span>
        </div>
      )}

      {result && !busy && (
        result.error ? (
          <div className="bg-white border border-zinc-200 p-6 font-mono text-sm whitespace-pre-wrap">{result.raw}</div>
        ) : (
          <>
            <div className="bg-zinc-900 text-white p-6 mb-4">
              <p className="label-caps text-zinc-400 mb-2">Best Match</p>
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-4">
                <div className="flex items-baseline gap-3">
                  <span className="font-display font-black text-5xl tracking-tighter text-[#1ABEDC] font-mono">{result.code}</span>
                  <span className="text-xs font-mono px-2 py-1 bg-zinc-700">{result.type}</span>
                </div>
                <div>
                  <span className="text-zinc-400 text-sm mr-2">GST Rate</span>
                  <span className="font-display font-black text-3xl">{result.gst_rate}%</span>
                </div>
              </div>
              <p className="text-zinc-300 mb-4">{result.description}</p>
              <div className="flex gap-2">
                <button onClick={() => copy(result.code)} data-testid="copy-hsn" className="bg-white text-zinc-900 px-3 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-zinc-100"><Copy size={14} /> Copy code</button>
                <button onClick={() => copy(`${result.code} - ${result.description} (${result.gst_rate}% GST)`)} className="border border-zinc-700 text-white px-3 py-2 text-sm font-semibold hover:bg-zinc-800">Copy full</button>
              </div>
            </div>

            {result.alternatives?.length > 0 && (
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3 border-b border-zinc-200">
                  <p className="label-caps">Alternative matches</p>
                </div>
                <table className="w-full">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="text-left px-6 py-2 label-caps">Code</th>
                      <th className="text-left px-6 py-2 label-caps">Description</th>
                      <th className="text-right px-6 py-2 label-caps">GST</th>
                      <th className="text-right px-6 py-2 label-caps"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.alternatives.map((a, i) => (
                      <tr key={i} className="border-b border-zinc-100">
                        <td className="px-6 py-2 font-mono font-bold">{a.code}</td>
                        <td className="px-6 py-2 text-sm">{a.description}</td>
                        <td className="px-6 py-2 text-right font-mono">{a.gst_rate}%</td>
                        <td className="px-6 py-2 text-right">
                          <button onClick={() => copy(a.code)} className="p-1.5 hover:bg-zinc-100"><Copy size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}
